import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Progress,
  Badge,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { FaBatteryFull, FaChartLine, FaHome, FaCheckCircle } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

const SolarBatteryROICalculator: React.FC = () => {
  const [inputs, setInputs] = useState({
    systemSize: 6.6,
    batteryCapacity: 10,
    dailyUsage: 20,
    feedInTariff: 0.08,
    batteryCost: 10000,
    electricityRate: 0.30,
  });

  const [results, setResults] = useState<any>(null);

  const handleInputChange = (field: string, value: any) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const calculate = () => {
    const { systemSize, batteryCapacity, dailyUsage, feedInTariff, batteryCost, electricityRate } =
      inputs;

    // Daily solar generation (kWh) - 4 hours peak sun
    const dailyGeneration = systemSize * 4;

    // Without battery - excess sold to grid
    const dailySelfConsumption = Math.min(dailyGeneration * 0.3, dailyUsage);
    const dailyExport = dailyGeneration - dailySelfConsumption;
    const annualSavingsNoBattery =
      dailySelfConsumption * electricityRate * 365 + dailyExport * feedInTariff * 365;

    // With battery - store excess for night use
    const batteryStorageUsed = Math.min(dailyExport, batteryCapacity);
    const additionalSelfConsumption = Math.min(batteryStorageUsed, dailyUsage - dailySelfConsumption);
    const newDailyExport = dailyExport - batteryStorageUsed;

    const annualSavingsWithBattery =
      (dailySelfConsumption + additionalSelfConsumption) * electricityRate * 365 +
      newDailyExport * feedInTariff * 365;

    const annualBatterySavings = annualSavingsWithBattery - annualSavingsNoBattery;
    const batteryROI = (annualBatterySavings / batteryCost) * 100;
    const paybackYears = batteryCost / annualBatterySavings;

    const selfConsumptionPercent =
      ((dailySelfConsumption + additionalSelfConsumption) / dailyGeneration) * 100;
    const gridIndependencePercent =
      ((dailySelfConsumption + additionalSelfConsumption) / dailyUsage) * 100;

    setResults({
      batteryROI: batteryROI.toFixed(1),
      paybackYears: paybackYears.toFixed(1),
      annualBatterySavings: annualBatterySavings.toFixed(0),
      selfConsumptionPercent: selfConsumptionPercent.toFixed(0),
      gridIndependencePercent: gridIndependencePercent.toFixed(0),
      dailyGeneration: dailyGeneration.toFixed(1),
      dailySelfConsumption: (dailySelfConsumption + additionalSelfConsumption).toFixed(1),
      annualSavingsNoBattery: annualSavingsNoBattery.toFixed(0),
      annualSavingsWithBattery: annualSavingsWithBattery.toFixed(0),
    });
  };

  const reset = () => {
    setInputs({
      systemSize: 6.6,
      batteryCapacity: 10,
      dailyUsage: 20,
      feedInTariff: 0.08,
      batteryCost: 10000,
      electricityRate: 0.30,
    });
    setResults(null);
  };

  const batteryOptions = [
    { capacity: 5, cost: 6000 },
    { capacity: 10, cost: 10000 },
    { capacity: 13.5, cost: 13500 },
    { capacity: 20, cost: 18000 },
  ];

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/battery-roi"
        fallbackTitle="Solar Battery ROI Calculator"
        fallbackDescription="Calculate the return on investment for adding battery storage to your solar system"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-r, yellow.400, green.400)"
        color="white"
        py={16}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={4} textAlign="center">
            <Icon as={FaBatteryFull} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Solar Battery ROI Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              Calculate your battery storage return on investment and energy independence
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Tabbed Layout */}
      <Container maxW="container.xl" py={12}>
        <Tabs variant="enclosed" colorScheme="green">
          <TabList>
            <Tab fontWeight="semibold">System Details</Tab>
            <Tab fontWeight="semibold">Battery Specs</Tab>
            <Tab fontWeight="semibold">Results</Tab>
          </TabList>

          <TabPanels>
            {/* TAB 1: System Details */}
            <TabPanel>
              <Card>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <Heading size="md" color="green.600">
                      Your Solar System
                    </Heading>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <FormControl>
                        <FormLabel>Solar System Size (kW)</FormLabel>
                        <Input
                          type="number"
                          step="0.1"
                          value={inputs.systemSize}
                          onChange={(e) =>
                            handleInputChange("systemSize", parseFloat(e.target.value) || 0)
                          }
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Common sizes: 5kW, 6.6kW, 10kW
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Daily Electricity Usage (kWh)</FormLabel>
                        <Input
                          type="number"
                          value={inputs.dailyUsage}
                          onChange={(e) =>
                            handleInputChange("dailyUsage", parseFloat(e.target.value) || 0)
                          }
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Check your power bill
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Electricity Rate ($/kWh)</FormLabel>
                        <Input
                          type="number"
                          step="0.01"
                          value={inputs.electricityRate}
                          onChange={(e) =>
                            handleInputChange("electricityRate", parseFloat(e.target.value) || 0)
                          }
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Your buy rate from retailer
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Feed-in Tariff ($/kWh)</FormLabel>
                        <Input
                          type="number"
                          step="0.01"
                          value={inputs.feedInTariff}
                          onChange={(e) =>
                            handleInputChange("feedInTariff", parseFloat(e.target.value) || 0)
                          }
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          What you get paid for exports
                        </Text>
                      </FormControl>
                    </SimpleGrid>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* TAB 2: Battery Specs */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card>
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      <Heading size="md" color="green.600">
                        Battery Configuration
                      </Heading>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <FormControl>
                          <FormLabel>Battery Capacity (kWh)</FormLabel>
                          <Input
                            type="number"
                            step="0.5"
                            value={inputs.batteryCapacity}
                            onChange={(e) =>
                              handleInputChange("batteryCapacity", parseFloat(e.target.value) || 0)
                            }
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Usable capacity of battery
                          </Text>
                        </FormControl>

                        <FormControl>
                          <FormLabel>Battery Cost ($)</FormLabel>
                          <Input
                            type="number"
                            value={inputs.batteryCost}
                            onChange={(e) =>
                              handleInputChange("batteryCost", parseFloat(e.target.value) || 0)
                            }
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Installed cost including inverter
                          </Text>
                        </FormControl>
                      </SimpleGrid>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <Heading size="sm" mb={4} color="gray.700">
                      Popular Battery Options
                    </Heading>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Capacity</Th>
                          <Th>Typical Cost</Th>
                          <Th>Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {batteryOptions.map((option) => (
                          <Tr key={option.capacity}>
                            <Td>{option.capacity} kWh</Td>
                            <Td>${option.cost.toLocaleString()}</Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="green"
                                variant="outline"
                                onClick={() => {
                                  handleInputChange("batteryCapacity", option.capacity);
                                  handleInputChange("batteryCost", option.cost);
                                }}
                              >
                                Use
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* TAB 3: Results */}
            <TabPanel>
              {results ? (
                <VStack spacing={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <Card bg="green.50" borderWidth={2} borderColor="green.400">
                      <CardBody>
                        <Stat textAlign="center">
                          <StatLabel>Battery ROI</StatLabel>
                          <StatNumber fontSize="4xl" color="green.600">
                            {results.batteryROI}%
                          </StatNumber>
                          <StatHelpText>Annual return</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card bg="blue.50" borderWidth={2} borderColor="blue.400">
                      <CardBody>
                        <Stat textAlign="center">
                          <StatLabel>Payback Period</StatLabel>
                          <StatNumber fontSize="4xl" color="blue.600">
                            {results.paybackYears} yrs
                          </StatNumber>
                          <StatHelpText>Break-even time</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card bg="yellow.50" borderWidth={2} borderColor="yellow.400">
                      <CardBody>
                        <Stat textAlign="center">
                          <StatLabel>Annual Savings</StatLabel>
                          <StatNumber fontSize="4xl" color="yellow.700">
                            ${parseInt(results.annualBatterySavings).toLocaleString()}
                          </StatNumber>
                          <StatHelpText>Additional with battery</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Heading size="sm" color="gray.700">
                          Energy Independence Metrics
                        </Heading>

                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <HStack>
                              <Icon as={FaHome} color="green.500" />
                              <Text fontWeight="semibold">Self-Consumption</Text>
                            </HStack>
                            <Badge colorScheme="green" fontSize="md">
                              {results.selfConsumptionPercent}%
                            </Badge>
                          </HStack>
                          <Progress
                            value={parseFloat(results.selfConsumptionPercent)}
                            colorScheme="green"
                            size="lg"
                            borderRadius="md"
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Percentage of solar energy you use vs export
                          </Text>
                        </Box>

                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <HStack>
                              <Icon as={FaCheckCircle} color="blue.500" />
                              <Text fontWeight="semibold">Grid Independence</Text>
                            </HStack>
                            <Badge colorScheme="blue" fontSize="md">
                              {results.gridIndependencePercent}%
                            </Badge>
                          </HStack>
                          <Progress
                            value={parseFloat(results.gridIndependencePercent)}
                            colorScheme="blue"
                            size="lg"
                            borderRadius="md"
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Percentage of your usage covered by solar + battery
                          </Text>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Card>
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Heading size="sm" color="gray.700">
                            Without Battery
                          </Heading>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Annual Savings:</Text>
                            <Text fontWeight="semibold">
                              ${parseInt(results.annualSavingsNoBattery).toLocaleString()}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            Excess energy exported to grid
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg="green.50">
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <HStack justify="space-between">
                            <Heading size="sm" color="green.700">
                              With Battery
                            </Heading>
                            <Icon as={FaChartLine} color="green.500" />
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Annual Savings:</Text>
                            <Text fontWeight="semibold" color="green.600">
                              ${parseInt(results.annualSavingsWithBattery).toLocaleString()}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600">
                            Excess stored for night use
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card>
                    <CardBody>
                      <VStack spacing={3} align="stretch">
                        <Heading size="sm" color="gray.700">
                          Daily Energy Flow
                        </Heading>
                        <HStack justify="space-between">
                          <Text fontSize="sm">Solar Generation:</Text>
                          <Text fontWeight="semibold">{results.dailyGeneration} kWh/day</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm">Self-Consumption:</Text>
                          <Text fontWeight="semibold" color="green.600">
                            {results.dailySelfConsumption} kWh/day
                          </Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm">Your Daily Usage:</Text>
                          <Text fontWeight="semibold">{inputs.dailyUsage} kWh/day</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

                  <HStack spacing={4}>
                    <Button colorScheme="green" size="lg" onClick={calculate} flex={1}>
                      Recalculate
                    </Button>
                    <Button variant="outline" size="lg" onClick={reset}>
                      Reset
                    </Button>
                  </HStack>

                  <LeadCaptureForm
                    calculatorType={CalculatorType.SolarBatteryRoi}
                    calculatorName="Solar Battery ROI Calculator"
                    calculatorPath="/calculators/solar-battery-roi"
                    inputData={inputs}
                    resultData={results}
                    hasResults={!!results}
                    accentColor="green"
                  />
                </VStack>
              ) : (
                <Card h="400px">
                  <CardBody>
                    <VStack spacing={4} justify="center" h="full" color="gray.400">
                      <Icon as={FaBatteryFull} boxSize={16} />
                      <Text fontSize="lg" textAlign="center">
                        Complete System Details and Battery Specs tabs, then click Calculate
                      </Text>
                      <Button colorScheme="green" onClick={calculate}>
                        Calculate ROI
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolarBatteryROICalculator;
